import { useEffect, useRef, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOnlineStatus } from './useOnlineStatus'
import { drainQueue } from '../lib/mutationQueue'
import { mutationQueueSize } from '../lib/offlineStore'
import { pendingCaptures } from '../lib/offlineStore'
import { drainCaptures } from '../lib/offlineCaptures'
import { apiGet, USE_MOCK_API } from '../api/httpClient'
import type { TripPoint, TripLink } from '../api/types'

interface SyncDelta {
  points: TripPoint[]
  links: TripLink[]
  server_time: string
}

const LAST_SYNC_KEY = (tripId: string) => `st:last-sync:${tripId}`

function readLastSync(tripId: string): string | null {
  try {
    return localStorage.getItem(LAST_SYNC_KEY(tripId))
  } catch {
    return null
  }
}

function writeLastSync(tripId: string, ts: string): void {
  try {
    localStorage.setItem(LAST_SYNC_KEY(tripId), ts)
  } catch {
    /* ignore */
  }
}

export interface SyncQueueState {
  /** Number of mutations still waiting to be sent. */
  pendingCount: number
  /** True while the drain + delta-fetch is running. */
  syncing: boolean
  /** Timestamp of the last successful sync (ISO string). */
  lastSyncAt: string | null
  /** Manually trigger a sync (useful for a "Sync now" button). */
  triggerSync: () => void
}

/**
 * Monitors network status for a trip.
 * - When online:  drains the local mutation queue, then pulls delta from server.
 * - When offline: counts pending mutations so the UI can show a badge.
 *
 * Invalidates React Query caches after a successful sync so fresh data is shown.
 */
export function useSyncQueue(tripId: string | undefined): SyncQueueState {
  const online    = useOnlineStatus()
  const qc        = useQueryClient()
  const [pending, setPending] = useState(0)
  const [syncing, setSyncing] = useState(false)
  const [lastSync, setLastSync] = useState<string | null>(
    tripId ? readLastSync(tripId) : null,
  )
  const syncRef = useRef(false) // prevent concurrent runs

  const refreshCount = useCallback(async () => {
    if (!tripId) return
    try { setPending((await mutationQueueSize(tripId)) + (await pendingCaptures(tripId)).length) }
    catch { setPending(await mutationQueueSize(tripId)) }
  }, [tripId])

  const sync = useCallback(async () => {
    if (!tripId || syncRef.current || !navigator.onLine) return
    syncRef.current = true
    setSyncing(true)

    try {
      // 1. Replay queued mutations
      await drainQueue(tripId)
      await drainCaptures(tripId)
      await refreshCount()

      if (USE_MOCK_API) {
        await qc.invalidateQueries({ queryKey: ['media', tripId] })
        await qc.invalidateQueries({ queryKey: ['documents', tripId] })
        await qc.invalidateQueries({ queryKey: ['points', tripId] })
        return
      }

      // 2. Pull server delta
      const since = readLastSync(tripId)
      const params = since ? `?since=${encodeURIComponent(since)}` : ''
      const delta = await apiGet<SyncDelta>(`/trips/${tripId}/sync${params}`)

      // 3. Stamp last sync
      writeLastSync(tripId, delta.server_time)
      setLastSync(delta.server_time)

      // 4. Invalidate affected queries so UI refreshes
      if (delta.points.length > 0)
        await qc.invalidateQueries({ queryKey: ['points', tripId] })
      if (delta.links.length > 0)
        await qc.invalidateQueries({ queryKey: ['links', tripId] })
      await qc.invalidateQueries({ queryKey: ['media', tripId] })
      await qc.invalidateQueries({ queryKey: ['documents', tripId] })
      await qc.invalidateQueries({ queryKey: ['today', tripId] })
    } catch {
      // Sync failed — will retry on next reconnect
    } finally {
      syncRef.current = false
      setSyncing(false)
    }
  }, [tripId, qc, refreshCount])

  // Count pending on mount and whenever tripId changes
  useEffect(() => {
    refreshCount()
  }, [refreshCount])

  // Trigger sync on reconnect
  useEffect(() => {
    if (online) sync()
  }, [online, sync])

  useEffect(() => {
    if (!online) return
    const id = setInterval(() => { if (pending > 0) void sync() }, 30_000)
    return () => clearInterval(id)
  }, [online, pending, sync])

  // Poll pending count so newly captured files and notes appear immediately.
  useEffect(() => {
    const id = setInterval(refreshCount, 5_000)
    return () => clearInterval(id)
  }, [online, refreshCount])

  return {
    pendingCount: pending,
    syncing,
    lastSyncAt: lastSync,
    triggerSync: sync,
  }
}
